/**
 * INSPO SERVICE (web)
 *
 * Web v1.1 surface: list + delete. Add / edit stay on mobile in v1.1 —
 * the mobile workflow (screenshot → tag polishes → save) is the primary
 * path. Web is here so users can prune from either device.
 *
 * Also deletes the associated Storage photo when the inspo doc goes,
 * so storage quota is released. Mobile's deleteInspo doesn't do this
 * yet; the disparity is fine because the doc-level delete is what the
 * user experiences, and the daily storage-usage sync (or an explicit
 * recalc) will reclaim the leaked file.
 */

import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
} from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import type { Inspo } from '../types/inspo';

export async function getUserInspo(uid: string): Promise<Inspo[]> {
  const snap = await getDocs(collection(db, 'users', uid, 'inspo'));
  const items = snap.docs.map((d) => ({ id: d.id, ...d.data() } as Inspo));
  return items.sort((a, b) => {
    const aMs = a.createdAt?.toMillis?.() ?? 0;
    const bMs = b.createdAt?.toMillis?.() ?? 0;
    return bMs - aMs;
  });
}

export async function getInspo(uid: string, inspoId: string): Promise<Inspo | null> {
  const snap = await getDoc(doc(db, 'users', uid, 'inspo', inspoId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as Inspo;
}

/**
 * Delete the inspo doc + its Storage photo. Photo deletion is best-
 * effort — a missing file (already gone, or path we can't derive)
 * shouldn't block the user from removing their inspo.
 */
export async function deleteInspo(uid: string, inspo: Inspo): Promise<void> {
  await deleteDoc(doc(db, 'users', uid, 'inspo', inspo.id));

  const photoUrl = inspo.photo?.url;
  if (!photoUrl) return;
  try {
    // Firebase Storage download URLs have the shape
    //   https://firebasestorage.googleapis.com/v0/b/<bucket>/o/<encoded-path>?alt=media&token=...
    // The `o/<encoded-path>` segment is what `ref()` expects (URL-decoded).
    const match = photoUrl.match(/\/o\/([^?]+)/);
    if (!match) return;
    const decodedPath = decodeURIComponent(match[1]);
    await deleteObject(ref(storage, decodedPath));
  } catch (err) {
    // Photo may already be gone (mobile v1 doesn't delete the file
    // on delete, so a re-delete from web after mobile deleted the
    // doc is a legitimate not-found). Log and continue.
    console.warn('[inspoService] photo cleanup failed', err);
  }
}
